import React, { useMemo, useRef, useState } from "react";

import { Box, IconButton, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  ColumnFilter,
  GeneralActions,
  SearchSuggest,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import Button, { Color } from "Components/Form/Button";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { useAppState } from "Context/StateContext";
import * as dot from "dot-wild";
import { Add as IconAdd, Eye as IconView } from "iconsax-react";
import { Location } from "Models/location";
import { Member as IMember } from "Models/members/member";
import { SearchAttribute } from "Models/search.attribute";
import { Filters } from "Models/shipeezi.table";
import { UserCompany } from "Models/user.company";
import { useTranslation } from "react-i18next";
import RoleService from "Services/role.service";
import { Status, UserTypes } from "Util/enums";
import { TypeOfMember } from "Util/enums/members";
import { filterSearch } from "Util/filterSearch";
import { onSearch } from "Util/onSearch";
import { paginate } from "Util/paginate";
import { RoleKeys } from "Util/queries";

import {
  MemberShowDrawer,
  MemberShowDrawerHandles,
} from "../../../../../../People/Members/Member/components/MemberShowDrawer";
import { getTypes } from "../../../../../../People/Users/types";
import { useEquipmentContext } from "../../../context/Equipment";
import { EmptyState } from "../EmptyState";
import { AddMemberModal } from "./AddMemberModal";
import useStyles from "./styles";

export const Member: React.FC = () => {
  const { equipment, onUpdateEquipment, setEquipment } = useEquipmentContext();
  const [isAddMemberModalOpened, setIsAddMemberModalOpened] = useState(false);

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const memberShowDrawerRef = useRef<MemberShowDrawerHandles>(null);

  const { currentUser, isMovingVersion, isFMVersion } = useAppState();

  const membersAdded = useMemo(() => {
    return equipment?.members || [];
  }, [equipment]);

  const filtersMatcher = (object: any, filter: Filters): boolean => {
    const [filterKey, filterValues] = Object.entries(filter)[0];

    if (dot.has(object, filterKey)) {
      const value = dot.get(object, filterKey);

      return filterValues.some((filter) => filter === value);
    }

    return false;
  };

  const membersRenderable = useMemo(() => {
    let data = membersAdded || [];

    if (searchAttributes.length > 0) data = filterSearch(data, searchAttributes);

    if (filters) {
      for (const [filterKey, filterValue] of Object.entries(filters)) {
        data = data?.filter((dataItem) => filtersMatcher(dataItem, { [filterKey]: filterValue }));
      }
    }

    setTotal(data.length);

    return paginate(data, page, pageSize);
  }, [membersAdded, searchAttributes, page, pageSize, filters]);

  const onAddMembersSelected = (members: IMember[]) => {
    const map: Map<number, IMember> = new Map();

    members?.forEach((member) => {
      if (member.id) {
        map.set(member.id, member);
      }
    });

    onUpdateEquipment("members", Array.from(map.values()));
  };

  const onRemoveMemberSelected = (memberId: number) => {
    setEquipment((prev) => ({
      ...(prev || {}),
      members: (prev?.members || []).filter((member) => Number(member.id) !== Number(memberId)),
    }));
  };

  const { data: rolesList } = useQuery({
    queryKey: RoleKeys.getAll([]),
    queryFn: async () => {
      const response = await RoleService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response && response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const userTypes = getTypes(isMovingVersion, isFMVersion);

  const columns: Column[] = [
    {
      name: "userCompany",
      title: translate("code"),
      formatText: (userCompany?: UserCompany) => userCompany?.code || "-",
    },
    { name: "fullName", title: translate("name") },
    { name: "email", title: translate("email") },
    { name: "typeOfMember", title: translate("typeOfMember") },
    {
      name: "userCompany",
      title: translate("typeOfUser"),
      formatText: (userCompany?: UserCompany) => userCompany?.typeUser || "-",
    },
    {
      name: "userCompany",
      title: `${translate("role")}(${translate("security").toLowerCase()})`,
      formatText: (userCompany?: UserCompany) => userCompany?.role?.name || "-",
    },
    {
      name: "location",
      title: translate("location"),
      formatText: (location?: Location) =>
        location?.country || location?.state || location?.suburb || location?.addressLine1 || "-",
    },
  ];

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: "fullName", title: translate("name") },
          { name: "email", title: translate("email") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder={translate("search")}
      />
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: ["newRecord"],
    },
    searchSuggest: renderSearchSuggest(),
    handleFilterBy: {
      categories: [
        {
          name: translate("typeOfMember"),
          column: "typeOfMember",
          options: [TypeOfMember.STAFF, TypeOfMember.USER],
        },
        {
          name: translate("role"),
          column: "userCompany.role.name",
          options: rolesList?.map((role) => role.name) || [],
        },
        {
          name: translate("typeOfUser"),
          column: "userCompany.typeUser",
          options:
            userTypes
              ?.filter((type) => type.name !== UserTypes.STAFF)
              ?.map((userType) => userType.name) || [],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: (
      <Button
        startIcon={<IconAdd size={20} />}
        customColor={Color.SECONDARY_DARK}
        onClick={() => setIsAddMemberModalOpened(true)}
      >
        {translate("addMember")}
      </Button>
    ),
  };

  return (
    <Box className={classes.main}>
      {membersAdded?.length ? (
        <ShipeeziNewGrid
          data={membersRenderable}
          columns={columns}
          totalItems={total}
          loadingData={false}
          emptyData={translate("noMembersToShow")}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          currentPage={page}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          positionActionsColumn="right"
          generalActions={generalActions}
          rowActionsExtra={[
            {
              title: translate("view"),
              key: "view",
              buttonAndAction: (_, member: IMember) => (
                <Tooltip title={translate("view")} placement="top">
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => memberShowDrawerRef.current?.open(member)}
                  >
                    <IconView size={20} color={colors.gray600} />
                  </IconButton>
                </Tooltip>
              ),
            },
            {
              title: translate("remove"),
              key: "remove",
              buttonAndAction: (memberId) => (
                <Tooltip title={translate("remove")} placement="top">
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => onRemoveMemberSelected(memberId)}
                  >
                    <IconCloseCircle width={20} height={20} color={colors.error500} />
                  </IconButton>
                </Tooltip>
              ),
            },
          ]}
          showSort={false}
        />
      ) : (
        <EmptyState
          descriptionResource={translate("members").toLowerCase()}
          buttonResource={translate("addMember")}
          onAddNew={() => setIsAddMemberModalOpened(true)}
        />
      )}
      <AddMemberModal
        isOpened={isAddMemberModalOpened}
        onClose={() => setIsAddMemberModalOpened(false)}
        membersIdAdded={membersAdded.map((member) => Number(member.id))}
        onConfirmAddMembers={onAddMembersSelected}
        rolesList={rolesList}
        userTypes={userTypes}
      />
      <MemberShowDrawer ref={memberShowDrawerRef} />
    </Box>
  );
};
